import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { useEnvironments } from '@/api/hooks';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useEnvironment } from '@/environment/EnvironmentProvider';
import { ENVIRONMENT_STYLE } from '@/environment/store';
import type { ApiError } from '@/api/client';
import type { UserSummary } from '@/types/api';

/** What the server assigns — mirrored here only to say it before it happens. */
const DEFAULT_ROLE = 'User';

interface AssignUserDialogProps {
  user: UserSummary | null;
  pending: boolean;
  error: ApiError | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation for making a read-only directory user editable.
 *
 * The action adds the user to SEP's `assigned_users` — the same as adding them
 * in the "Assign Users" tool — which is what gives them the SEP id the tenant
 * and role dropdowns write against. Reversible in the sense that tenant and role
 * can be changed afterwards, but it creates a row in SEP that this dashboard
 * has no way to remove again, so it is confirmed, and the dialog says plainly
 * who to ask when in doubt.
 *
 * Like kickout, it stays open on failure: an error that reads "SEP accepted the
 * user but does not list them" needs to be read, not missed in a toast.
 */
export function AssignUserDialog({
  user,
  pending,
  error,
  onCancel,
  onConfirm,
}: AssignUserDialogProps) {
  const isMobile = useIsMobile();
  const environment = useEnvironment();
  const { data: environments } = useEnvironments();

  if (!user) return null;

  const style = ENVIRONMENT_STYLE[environment];
  const baseUrl = environments?.find((info) => info.key === environment)?.apiBaseUrl;
  const toolUrl = baseUrl ? `${baseUrl.replace(/\/+$/, '')}/api/assigned-users` : null;

  return (
    <Dialog
      open
      onClose={pending ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>Benutzer bearbeitbar machen</DialogTitle>
      {pending && <LinearProgress />}
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            <strong>{user.username}</strong> wird in SEP zu den zugewiesenen Benutzern
            hinzugefügt. Danach lassen sich Mandant und Rolle hier ändern.
          </Typography>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Übernommen werden die vorkonfigurierten Einstellungen:
            </Typography>
            <Typography component="ul" variant="body2" sx={{ m: 0, pl: 3 }}>
              <li>
                Rolle: <strong>{DEFAULT_ROLE}</strong>
              </li>
              <li>
                Mandant: <strong>{user.tenant}</strong> — wie beim Onboarding festgelegt
              </li>
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Das entspricht dem Hinzufügen im Tool «Assign Users» der Umgebung{' '}
            <strong>{style.label}</strong>
            {toolUrl && (
              <>
                {' '}
                (
                <Link href={toolUrl} target="_blank" rel="noopener noreferrer">
                  {toolUrl}
                </Link>
                )
              </>
            )}
            . Staging und Development haben je ein eigenes.
          </Typography>

          <Alert severity="warning">
            Unsicher, was das bewirkt? Dann bitte vorher das Dev-Team fragen.
          </Alert>

          {error && <Alert severity="error">{error.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={pending} sx={{ textTransform: 'none' }}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          disabled={pending}
          onClick={onConfirm}
          sx={{ textTransform: 'none' }}
        >
          {pending ? 'Wird hinzugefügt…' : 'Bearbeitbar machen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
